/**
 * PersonaValidationSection
 *
 * Displays persona validation results comparing content targeting
 * against the product's price tier.
 *
 * Part of Story 51: Price-Segment Persona Validation
 *
 * Date: December 23, 2025
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Users,
  DollarSign,
  Target,
  Lightbulb,
} from 'lucide-react';
import {
  PersonaValidationReport,
  validatePersona,
  getPersonaReport,
  getPersonaSeverityColor,
  getPersonaAlignmentColor,
  getTierBadgeVariant,
} from '@/services/contentReviewService';

interface PersonaValidationSectionProps {
  generationId: string;
  autoRun?: boolean;
}

export function PersonaValidationSection({
  generationId,
  autoRun = false,
}: PersonaValidationSectionProps) {
  const [report, setReport] = useState<PersonaValidationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [skipReason, setSkipReason] = useState<string | null>(null);

  // Load existing report on mount
  useEffect(() => {
    async function loadExistingReport() {
      try {
        const response = await getPersonaReport(generationId);
        if (response.data) {
          setReport(response.data);
          setHasChecked(true);
        } else if (autoRun) {
          handleRunValidation();
        }
      } catch (err) {
        console.error('Error loading persona report:', err);
        if (autoRun) {
          handleRunValidation();
        }
      }
    }

    loadExistingReport();
  }, [generationId, autoRun]);

  async function handleRunValidation() {
    setLoading(true);
    setError(null);
    setSkipped(false);

    try {
      const response = await validatePersona(generationId);
      if (response.skipped) {
        setSkipped(true);
        setSkipReason(response.reason || 'Validation skipped');
        setHasChecked(true);
      } else if (response.data) {
        setReport(response.data);
        setHasChecked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run validation');
    } finally {
      setLoading(false);
    }
  }

  // Render alignment icon
  const AlignmentIcon = () => {
    if (!report) return null;

    if (report.isAligned && report.alignmentScore >= 80) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (report.isAligned) {
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
    if (report.severity === 'CRITICAL') {
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
    return <AlertTriangle className="h-5 w-5 text-orange-500" />;
  };

  // If no check has been run yet
  if (!hasChecked && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Persona Validation
          </CardTitle>
          <CardDescription>
            Validate content targets appropriate buyer personas for the price tier
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRunValidation} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Validating...
              </>
            ) : (
              'Validate Persona Targeting'
            )}
          </Button>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Validating Persona Targeting...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Analyzing content tone and comparing against price tier expectations...
          </p>
        </CardContent>
      </Card>
    );
  }

  // Skipped state
  if (skipped) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-5 w-5" />
            Persona Validation Skipped
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{skipReason}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && !report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-500">
            <AlertCircle className="h-5 w-5" />
            Persona Validation Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Report display
  if (!report) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlignmentIcon />
            Persona Validation
          </CardTitle>
          <div className="flex items-center gap-2">
            {report.severity && (
              <Badge variant={getPersonaSeverityColor(report.severity)}>
                {report.severity}
              </Badge>
            )}
            <Badge variant={report.isAligned ? 'default' : 'destructive'}>
              {report.isAligned ? 'Aligned' : 'Misaligned'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunValidation}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>
          {report.mismatchReason || 'Content persona targeting matches price tier'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Alignment Score */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={getPersonaAlignmentColor(report.isAligned, report.alignmentScore)}>
              Alignment Score: {report.alignmentScore}%
            </span>
            <span className="text-muted-foreground">
              Target: 70%+
            </span>
          </div>
          <Progress value={report.alignmentScore} className="h-3" />
        </div>

        {/* Price Tier Info */}
        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Price Tier</span>
            </div>
            <Badge variant={getTierBadgeVariant(report.priceTier)}>
              {report.priceTierDisplay}
            </Badge>
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            Product Price: {report.productPrice?.toFixed(2) || 'N/A'}
            {report.category && ` | Category: ${report.category}`}
          </div>
        </div>

        {/* Valid Personas for Tier */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Target className="h-4 w-4 text-green-500" />
            Valid Personas for {report.priceTierDisplay}
          </h4>
          <div className="flex flex-wrap gap-2">
            {report.validPersonas.map((persona, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {persona.name}
              </Badge>
            ))}
          </div>
        </div>

        {/* Detected Personas */}
        {report.detectedPersonas && report.detectedPersonas.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              Detected in Content
            </h4>
            <div className="space-y-2">
              {report.detectedPersonas.map((detected, index) => {
                const isValid = report.validPersonas.some(
                  v => v.id === detected.persona
                );
                return (
                  <div
                    key={index}
                    className={`p-2 rounded-lg border ${
                      isValid
                        ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'
                        : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isValid ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-sm font-medium">
                          {detected.personaName || detected.persona}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {Math.round(detected.confidence * 100)}% confident
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {detected.impliedTier}
                        </Badge>
                      </div>
                    </div>
                    {detected.evidence && detected.evidence.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Evidence: "{detected.evidence.slice(0, 2).join('", "')}"
                        {detected.evidence.length > 2 && ` (+${detected.evidence.length - 2} more)`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tone Analysis */}
        {report.overallTone && (
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium">Overall Tone: {report.overallTone}</span>
            </div>
            {report.toneExplanation && (
              <p className="text-xs text-muted-foreground">{report.toneExplanation}</p>
            )}
          </div>
        )}

        {/* Recommendations */}
        {report.recommendations && report.recommendations.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              Recommendations
            </h4>
            <ul className="space-y-1">
              {report.recommendations.map((rec, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-yellow-500 mt-0.5">&bull;</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          Validated: {new Date(report.validatedAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}
